import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Key, Check } from 'lucide-react';
import Button from '../components/Button';
import Input from '../components/Input';
import authService from '../services/authService';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const [formData, setFormData] = useState<ResetPasswordFormData>({
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    // Extrair token da URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    if (resetToken) {
      setToken(resetToken);
    } else {
      setError('Token de redefinição inválido ou expirado.');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validações
      if (!formData.password) {
        throw new Error('Por favor, informe sua nova senha');
      }

      if (formData.password.length < 8) {
        throw new Error('A senha deve ter pelo menos 8 caracteres');
      }

      if (formData.password !== formData.confirmPassword) {
        throw new Error('As senhas não coincidem');
      }

      if (!token) {
        throw new Error('Token de redefinição inválido');
      }

      const response = await authService.resetPassword({
        token: token,
        password: formData.password
      });

      if (response.success) {
        console.log('Password reset successful:', response.data);
        setIsSubmitted(true);
      } else {
        throw new Error(response.message || 'Erro ao redefinir senha');
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao redefinir senha. Tente novamente.');
      console.error('Reset password error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-3xl font-display font-bold text-brown-800 mb-4">
              Senha Redefinida!
            </h3>
            <p className="text-brown-600 mb-6">
              Sua senha foi redefinida com sucesso. Agora você pode fazer login
              com sua nova senha.
            </p>
            <a href="/signin">
              <Button variant="primary" className="w-full">
                Fazer Login
              </Button>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 via-cream-100 to-gold-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-brown-800 mb-2">
            SantoGram
          </h1>
          <p className="text-brown-600">
            Redefina sua senha
          </p>
        </div>

        {/* Reset Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl border border-cream-200">
          <div className="mb-6 text-center">
            <div className="w-12 h-12 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Key className="w-6 h-6 text-gold-600" />
            </div>
            <h2 className="text-2xl font-display font-bold text-brown-800 mb-2">
              Nova Senha
            </h2>
            <p className="text-brown-600 text-sm">
              Crie uma nova senha forte e segura para sua conta.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Password Field */}
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Nova senha (min. 8 caracteres)"
                label="Nova Senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-[52px] transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Confirm Password Field */}
            <div className="relative">
              <Input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirme sua nova senha"
                label="Confirmar Nova Senha"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-[52px] transform -translate-y-1/2 text-brown-400 hover:text-brown-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* Password requirements */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-700 text-sm">
                <strong>Sua nova senha deve ter:</strong><br />
                • Pelo menos 8 caracteres<br />
                • Letras maiúsculas e minúsculas<br />
                • Pelo menos um número<br />
                • Pelo menos um símbolo especial
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isSubmitting || !token}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Redefinindo...
                </>
              ) : (
                'Redefinir Senha'
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-brown-500">
            Lembre-se de usar uma senha única que você não utiliza em outros sites.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;